<template>
  <div
    class="persistent-error-toast-root"
    :style="{ '--progress-width': progressWidth + '%' }"
  >
    <div class="persistent-error-toast-header">
      <div class="persistent-error-toast-icon">
        <i :class="icon || 'fas fa-exclamation-triangle'"></i>
      </div>
      <div class="persistent-error-toast-title text-base md:text-lg">
        {{ title }}
      </div>
      <button
        class="persistent-error-toast-close"
        @click="$emit('close')"
        type="button"
        aria-label="Cerrar notificación"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="persistent-error-toast-body text-sm md:text-base">
      <span v-html="message"></span>
    </div>
    <div class="persistent-error-toast-progress">
      <div class="persistent-error-toast-progress-bar"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from 'vue';

  const props = defineProps<{
    title: string;
    message: string;
    duration?: number;
    icon?: string;
  }>();

  const emit = defineEmits<{
    close: [];
  }>();

  const progressWidth = ref(100);
  let progressInterval: number | null = null;

  const startProgress = () => {
    const duration = props.duration || 15000; // Default 15 seconds
    const updateInterval = 50; // Update every 50ms
    const totalSteps = duration / updateInterval;
    let currentStep = 0;

    progressInterval = window.setInterval(() => {
      currentStep++;
      progressWidth.value = Math.max(0, 100 - (currentStep / totalSteps) * 100);

      if (currentStep >= totalSteps) {
        clearInterval(progressInterval!);
        emit('close');
      }
    }, updateInterval);
  };

  onMounted(() => {
    startProgress();
  });

  onUnmounted(() => {
    if (progressInterval) {
      clearInterval(progressInterval);
    }
  });
</script>

<style scoped lang="scss">
  @use '@/app/assets/sass/fonts' as *;

  .persistent-error-toast-root {
    background: var(--color-white);
    border-radius: 16px;
    box-shadow:
      0 8px 32px rgba(244, 67, 54, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    max-height: 85vh;
    min-height: 100px;
    position: relative;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(244, 67, 54, 0.15);
    display: flex;
    flex-direction: column;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, var(--color-red) 0%, #ff6b6b 100%);
      z-index: 1;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow:
        0 12px 40px rgba(244, 67, 54, 0.16),
        0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  .persistent-error-toast-header {
    display: flex;
    align-items: flex-start;
    padding: 24px 24px 16px;
    gap: 14px;
    flex-shrink: 0;
  }

  .persistent-error-toast-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--color-red-light) 0%, #ffe0dd 100%);
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(244, 67, 54, 0.15);
  }

  .persistent-error-toast-icon .fas {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-red);
  }

  .persistent-error-toast-title {
    font-family: $bold-font;
    color: var(--color-black-flat);
    line-height: 1.4;
    flex: 1;
    padding-top: 2px;
  }

  .persistent-error-toast-close {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--color-gray-mid-2);
    flex-shrink: 0;

    &:hover {
      background: rgba(244, 67, 54, 0.08);
      color: var(--color-red);
    }

    &:active {
      transform: scale(0.92);
    }

    .fas {
      font-size: 14px;
    }
  }

  .persistent-error-toast-body {
    padding: 0 24px 24px 78px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.15);
      border-radius: 3px;

      &:hover {
        background: rgba(0, 0, 0, 0.25);
      }
    }
  }

  .persistent-error-toast-body :deep(*) {
    font-family: $medium-font;
    line-height: 1.6;
    margin: 0;
  }

  .persistent-error-toast-body :deep(p) {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Allow links to keep their global color (.link) by not overriding them */
  .persistent-error-toast-body :deep(:not(a)) {
    color: var(--color-gray-dark);
  }

  /* Style for links */
  .persistent-error-toast-body :deep(a) {
    color: var(--color-teal);
    text-decoration: underline;
    font-weight: 500;
    transition: color 0.2s ease;

    &:hover {
      color: var(--color-dark-teal-blue);
    }
  }

  /* Style for strong/bold text (like UUIDs) */
  .persistent-error-toast-body :deep(strong),
  .persistent-error-toast-body :deep(.bold-font) {
    font-family: $semi-bold-font;
    color: var(--color-black-flat);
    background: var(--color-gray-lightest);
    padding: 2px 6px;
    border-radius: 4px;
    letter-spacing: 0.3px;
  }

  /* Style for code blocks */
  .persistent-error-toast-body :deep(code) {
    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
    font-size: 13px;
    background: var(--color-gray-lightest);
    padding: 2px 6px;
    border-radius: 4px;
    color: var(--color-red);
  }

  .persistent-error-toast-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: rgba(244, 67, 54, 0.08);
    overflow: hidden;
    flex-shrink: 0;
  }

  .persistent-error-toast-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, var(--color-red) 0%, #ff6b6b 100%);
    width: var(--progress-width);
    transition: width 0.05s linear;
    box-shadow: 0 0 8px rgba(244, 67, 54, 0.4);
  }

  /* Responsive adjustments */
  @media (max-width: 640px) {
    .persistent-error-toast-root {
      max-height: 80vh;
    }

    .persistent-error-toast-header {
      padding: 20px 16px 12px;
      gap: 12px;
    }

    .persistent-error-toast-icon {
      width: 36px;
      height: 36px;
      border-radius: 10px;
    }

    .persistent-error-toast-icon .fas {
      font-size: 16px;
    }

    .persistent-error-toast-title {
      font-size: 15px;
    }

    .persistent-error-toast-body {
      padding: 0 16px 20px 64px;
    }

    .persistent-error-toast-body :deep(*) {
      font-size: 13px;
      line-height: 1.6;
    }

    .persistent-error-toast-body :deep(strong),
    .persistent-error-toast-body :deep(.bold-font) {
      font-size: 12px;
      padding: 1px 4px;
    }
  }
</style>
